/**
 * Async Billing Export Types
 * Matches backend types from billing-export-sam/src/handlers/billing/billing.types.ts
 */

export enum BillingExportStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  CompletedWithErrors = 'completed-with-errors',
  Failed = 'failed',
}

export enum BillingRowStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Error = 'error',
}

export interface AsyncExportResponse {
  requestId: string;
  status: string;
  message?: string;
}

export interface AsyncExportStatusResponse {
  requestId: string;
  status: string;
  totalRows: number;
  processedRows: number;
  progress: number;
  completedRows?: number;
  errorRows?: number;
  result?: {
    outputFileUrl?: string;
    errorFileUrl?: string;
    outputFileContent?: string;
    errorFileContent?: string;
  };
  errorMessage?: string;
}

export interface BillingExportListItem {
  requestId: string;
  status: string;
  totalRows: number;
  processedRows: number;
  progress: number;
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  errorMessage?: string;
}

export interface ListBillingExportsResponse {
  exports: BillingExportListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface BillingExportDetailResponse {
  requestId: string;
  clinicId: number;
  status: string;
  totalRows: number;
  processedRows: number;
  progress: number;
  completedRows: number;
  errorRows: number;
  pendingRows: number;
  parameters?: {
    fileVersion?: number;
    hl7Format?: boolean;
  };
  result?: {
    outputFileUrl?: string;
    errorFileUrl?: string;
    outputFileContent?: string;
    errorFileContent?: string;
  };
  userEmail?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingExportRowItem {
  id: number;
  rowNumber: number;
  status: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingExportRowDetailItem extends BillingExportRowItem {
  inputData?: BillingRowInputData;
  enrichedData?: BillingRowEnrichedData;
  outputData?: object;
}

export interface ListBillingExportRowsResponse {
  rows: BillingExportRowItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface BillingExportStats {
  total: number;
  byStatus: {
    pending: number;
    processing: number;
    completed: number;
    completedWithErrors: number;
    failed: number;
  };
  recentActivity: {
    today: number;
    last7Days: number;
    last30Days: number;
  };
  averageProcessingTimeMs?: number;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

export interface BillingExportStatsResponse {
  stats: BillingExportStats;
}

// Query parameter types for API calls
export interface ListBillingExportsParams {
  limit?: number;
  offset?: number;
  status?: BillingExportStatus | string;
  fromDate?: string;
  toDate?: string;
}

export interface ListBillingExportRowsParams {
  limit?: number;
  offset?: number;
  status?: BillingRowStatus | string;
  includeData?: boolean;
}

// Input data from CSV row (raw format)
export interface BillingRowInputData {
  rawRow: string[];
  specialColumns: {
    cpt: number;
    icd: number;
    drChronoChartId: number;
    enaraClaimNumber: number;
  };
}

// Enriched data after processing
export interface BillingRowEnrichedData {
  rows: Array<{
    fullName?: string;
    dateOfService?: string;
    cpt?: string;
    chart_id?: string;
    provider?: string;
    office?: string;
    icdCode1?: string;
    icdCode2?: string;
    icdCode3?: string;
    icdCode4?: string;
  }>;
  errors: Array<{ message?: string }>;
}

export interface GetBillingExportStatsParams {
  fromDate?: string;
  toDate?: string;
}
